import React, { useEffect, useState } from 'react'
import { Box, Button, Card, CardMedia, Typography, alpha } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { useDebounce } from '@e3dc-react/shell/Hooks/useDebounce'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { currencyFormatter } from '../../utils/formatter'
import { PRODUCT_CART_DEBOUNCE_TIMER } from '../../consts'
import { ProductType } from '../../types'
import { QuantityAvailabilityIndicator } from '../ShopProduct'
import { E3dcAvailability } from '../../models/power-meter.model'
import useModals from 'src/Hooks/useModals'
import useShopAdditionalProducts from '../../hooks/use-shop-additional-products'
import { useShopCartContext } from '../../context'
import { useShopChannelRules } from '../../hooks/use-shop-channel-type'
import ButtonQuantity from '../ShopProduct/ButtonQuantity'

interface ShopProductCategoryCardProps {
  productId: string
  title: string
  image: string
  price: number
  unitPrice: number
  priceApplied: boolean
  isChangeLoading?: boolean
  productType: string
  powerMeterType?: string | undefined
  productAvailability?: E3dcAvailability | undefined
  productRequiresAdditionalProducts: boolean
  minQtyOfAdditionalProducts: number | null
  productHint: string | false
  isProductHighlight?: boolean
  handleProductUpdate?: (productId: string, quantity: number) => void
  handleProductDelete?: (productId: string) => void
  handleProductAdd?: (
    productId: string,
    productType: string,
    requresAdditionalProducts: boolean,
    minQtyOfAdditionalProducts: number | null,
    productHint: string | false,
    powerMeterType?: string
  ) => void
}

interface StyleProps {
  isProductHighlight: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  card: {
    width: '100%',
    height: '100%',
    padding: 0,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    border: props.isProductHighlight ? `1px solid ${theme.palette.purple.main}` : '1px solid transparent',
    '&:hover': {
      cursor: 'pointer',
      border: `1px solid ${theme.palette.grey.grey05}`,
    },
  },
  cardActive: {
    border: `1.5px solid ${theme.palette.primary.main}`,
    boxShadow: 'none',
  },
  cardBackground: {
    margin: 0,
    padding: 0,
    objectFit: 'contain',
    width: '100%',
    minHeight: '202px',
    height: 'auto',
  },
  textContainer: {
    padding: `${theme.spacing(2)} ${theme.spacing(1.5)}`,
    whiteSpace: 'pre-wrap',
    paddingBottom: 0,
  },
  priceContainer: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  flexPrice: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  buttonContainer: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  button: {
    fontSize: '16px',
    padding: '16px 0px',
    alignSelf: 'center',
  },
  cardMediaContainer: {
    position: 'relative',
  },
  highlightText: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: alpha(theme.palette.purple.light, 0.3),
    textAlign: 'center',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: theme.palette.purple.main,
  },
}))

/*
 * This Component Shows a Card with just Important Information about product, Such as Image, Name , Price and Discount.
 * You can add also the product to the Cart directly from here or you can get into a more detailed Product Information.
 * This component is called when listening the Products for a category
 */
const ShopProductCategoryCard: React.VFC<ShopProductCategoryCardProps> = ({
  productId,
  title,
  image,
  price,
  unitPrice,
  priceApplied = false,
  isChangeLoading = false,
  isProductHighlight = false,
  productType,
  powerMeterType,
  productAvailability,
  productRequiresAdditionalProducts,
  productHint,
  minQtyOfAdditionalProducts,
  handleProductUpdate,
  handleProductDelete,
  handleProductAdd,
}) => {
  const navigate = useNavigate()
  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles({ isProductHighlight })
  const { cartData } = useShopCartContext()
  const { openShopAdditionalProductsModal } = useModals()
  const { requireRetrofitBatterySerialNumber } = useShopChannelRules()
  const { addAdditionalProductsToBasket } = useShopAdditionalProducts()
  const selectedProduct = cartData?.lineItems.find(cartProduct => cartProduct.referencedId === productId) ?? null
  const [productQuantity, setProductQuantity] = useState<number>(selectedProduct?.quantity || 0)
  const debouncedQuantity = useDebounce(productQuantity, PRODUCT_CART_DEBOUNCE_TIMER)
  const maxQuantity = selectedProduct?.quantityInformation?.maxPurchase
  const formattedUnitPrice = currencyFormatter(unitPrice)
  const formattedPrice = currencyFormatter(price)
  const productIsBatteryType = selectedProduct?.payload?.e3dc?.productType === ProductType.RetrofitBattery
  const isRetrofitBatteryThatRequiresSerialNumber = productIsBatteryType && requireRetrofitBatterySerialNumber

  useEffect(() => {
    if (selectedProduct && selectedProduct !== null && selectedProduct?.quantity !== productQuantity) {
      if (debouncedQuantity < 1) {
        handleProductDelete?.(selectedProduct.id)
      } else {
        handleProductUpdate?.(selectedProduct.id, debouncedQuantity)
      }
    }
  }, [debouncedQuantity]) // eslint-disable-line

  useEffect(() => {
    setProductQuantity(selectedProduct?.quantity ?? 0)
  }, [selectedProduct])

  const handleQuantityIncrease = (): void => {
    if (productRequiresAdditionalProducts && minQtyOfAdditionalProducts && selectedProduct) {
      return openShopAdditionalProductsModal({
        mainProductId: productId,
        mainProductQuantity: selectedProduct.quantity,
        minQtyOfAdditionalProducts,
        onConfirm: additionalProducts => {
          addAdditionalProductsToBasket(selectedProduct.id, additionalProducts, productQuantity + 1).then(result => {
            if (result) {
              setProductQuantity(result.mainProductQuantity)
            }
          })
        },
      })
    }
    setProductQuantity(productQuantity + 1)
  }

  const handleQuantityDecrease = (): void => {
    setProductQuantity(productQuantity - 1)
  }

  const handleQuantityChange = (quantity: number): void => {
    setProductQuantity(quantity)
  }

  return (
    <Box>
      <Card className={cx(classes.card, selectedProduct && classes.cardActive)}>
        <Box>
          <Box className={classes.cardMediaContainer}>
            <CardMedia
              onClick={() => {
                navigate(`/shop/product/${productId}`)
              }}
              className={classes.cardBackground}
              component="img"
              height="auto"
              image={image}
              alt={t('shop.product-category-card.image-alt')}
            />
            {isProductHighlight && (
              <Typography variant="h3" className={classes.highlightText}>
                {t('shop.product-category-card.highlight-label')}
              </Typography>
            )}
          </Box>
          <Box className={classes.textContainer}>
            <Typography variant="h3">
              {title}
              <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
            </Typography>
          </Box>
        </Box>
        <Box>
          <Box className={classes.priceContainer}>
            <Box className={classes.flexPrice}>
              <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
                {unitPrice ? formattedUnitPrice : formattedPrice}
              </Typography>
              {unitPrice < price && (
                <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                  {formattedPrice}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.buttonContainer}>
            <Button
              className={classes.button}
              size="large"
              color="inherit"
              variant="outlined"
              fullWidth
              onClick={() => {
                navigate(`/shop/product/${productId}`)
              }}
            >
              {t('shop.common.more-info')}
            </Button>
            {selectedProduct && selectedProduct !== null && productQuantity >= 1 && !isRetrofitBatteryThatRequiresSerialNumber ? (
              <ButtonQuantity
                quantity={productQuantity}
                onQuantityDecrease={() => handleQuantityDecrease()}
                onQuantityIncrease={() => handleQuantityIncrease()}
                onQuantityChange={quantity => handleQuantityChange(quantity)}
                maxQuantity={maxQuantity}
                isLoading={isChangeLoading}
              />
            ) : (
              <ButtonWithLoading
                className={classes.button}
                color={'primary'}
                disabled={!priceApplied}
                variant="contained"
                fullWidth
                isLoading={productQuantity > 0 && isChangeLoading}
                onClick={() => {
                  if (!isChangeLoading) {
                    setProductQuantity(1)
                    handleProductAdd?.(
                      productId,
                      productType,
                      productRequiresAdditionalProducts,
                      minQtyOfAdditionalProducts,
                      productHint,
                      powerMeterType
                    )
                  }
                }}
                label={t('shop.common.select')}
              ></ButtonWithLoading>
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  )
}

export default ShopProductCategoryCard
